import { useState, useEffect, useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { IconCategory, WorkspaceType } from '@deepguide-ai/types'
import { useAtom } from 'jotai'
import { ArrowRight } from 'lucide-react'
import { toast } from 'sonner'
import { activeWorkspaceWithStorageAtom } from '@/stores/workspaceStore'
import { trpc } from '@/lib/trpc'
import { useActiveOrganization } from '@/hooks/useActiveOrganization'
import { Button } from '@deepguide-ai/ui/button'
import { Input } from '@deepguide-ai/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { WORKSPACE_TYPE_CONFIGS, getWorkspaceTypeConfig } from '@/config/workspace-types'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { useFeatureFlags } from '@/hooks/useFeatureFlag'
import { frameworkOptions } from '@/lib/compliance-framework'

interface FormData {
  name?: string
  icon?: string
  IconCategory: IconCategory
  type?: WorkspaceType
}

export default function CreateWorkspace() {
  const { organization } = useActiveOrganization()
  const navigate = useNavigate()
  const [, setActiveWorkspace] = useAtom(activeWorkspaceWithStorageAtom)

  // Every new workspace is SCREENATA. Type picker is removed.
  const defaultWorkspaceType: WorkspaceType = 'SCREENATA'
  const shouldShowTypeSelector = false

  const [data, setData] = useState<FormData>({
    name: '',
    IconCategory: 'LUCIDE',
    icon: '',
    type: defaultWorkspaceType,
  })
  // RFC-231: frameworks are the one input setup cannot default, so they are
  // asked here — on the form, not in a wizard. SOC 2 preselected; GDPR needs
  // its RFC-222 scope tokens and is enrolled from Program settings instead.
  const { flags: frameworkFlags } = useFeatureFlags(['iso27001', 'iso42001'])
  const frameworkChoices = useMemo(
    () => frameworkOptions(frameworkFlags).filter((o) => o.value !== 'OTHER' && o.value !== 'GDPR'),
    [frameworkFlags],
  )
  const [frameworks, setFrameworks] = useState<string[]>(['SOC2'])

  // Get type-specific configuration
  const typeConfig = useMemo(() => getWorkspaceTypeConfig(data.type || 'COMPLIANCE'), [data.type])
  const TypeIcon = typeConfig.icon

  // Update workspace type when organization product changes
  useEffect(() => {
    setData((prev) => ({
      ...prev,
      type: defaultWorkspaceType,
    }))
  }, [defaultWorkspaceType])

  // Create workspace mutation
  const createWorkspaceMutation = useMutation({
    ...trpc.workspaces.createWorkspace.mutationOptions(),
    onSuccess: async (data) => {
      // Update global workspace state
      if (data?.workspace) {
        setActiveWorkspace(data.workspace)

        // Wait for localStorage to be updated before navigating
        // Poll until the workspace is actually stored, with timeout
        const workspaceId = data.workspace.id
        await new Promise<void>((resolve) => {
          let attempts = 0
          const maxAttempts = 50 // ~800ms at 60fps
          const checkStorage = () => {
            const stored = localStorage.getItem('auth:activeWorkspaceId')
            if (stored === workspaceId || attempts >= maxAttempts) {
              resolve()
            } else {
              attempts++
              requestAnimationFrame(checkStorage)
            }
          }
          checkStorage()
        })

        toast.success('Workspace created successfully')

        // RFC-231: land on the chat home — the Setup guide hero shows setup
        // streaming; there is no wizard to walk.
        navigate({
          to: '/workspaces/$workspaceId/',
          params: { workspaceId },
          search: {},
        })
      }
    },
    onError: (error) => {
      console.error('Failed to create workspace:', error)
      toast.error('Failed to create workspace')
    },
  })

  const handleSubmit = async () => {
    if (!data.name?.trim()) {
      toast.error('Workspace name is required')
      return
    }

    // Prepare workspace data (organizationId is handled by the backend from context)
    if (frameworks.length === 0) {
      toast.error('Pick at least one framework')
      return
    }
    const workspaceData: any = {
      title: data.name,
      type: 'SCREENATA',
      enabledFrameworks: frameworks,
      enabledFeatures: typeConfig.defaults.enabledFeatures || [],
    }

    createWorkspaceMutation.mutate(workspaceData)
  }

  const filterInput = (value: string) => {
    return value.replace(/[^a-zA-Z0-9_\-\s]/g, '')
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emojiRegex = /\p{Extended_Pictographic}/gu
    const name = filterInput(e.target.value.replace(emojiRegex, ''))
    setData({ ...data, name })
  }
  return (
    <div className='w-full max-w-[480px]'>
      <div className='mb-2 flex items-center justify-center gap-3'>
        <TypeIcon className={`h-8 w-8 ${typeConfig.iconColor}`} />
        <h1 className='text-center text-2xl leading-8 font-semibold'>
          {typeConfig.onboarding.title}
        </h1>
      </div>
      <p className='text-muted-foreground mb-12 text-center text-lg leading-7 font-semibold'>
        {typeConfig.onboarding.subtitle}
      </p>

      {/* Workspace Type - Only show if organization has no specific product */}
      {shouldShowTypeSelector && (
        <div className='mb-6 space-y-2'>
          <label
            htmlFor='type'
            className='block text-sm leading-[14px] font-medium'
          >
            Workspace Type
          </label>
          <Select
            value={data.type}
            onValueChange={(value) =>
              setData((prev) => ({ ...prev, type: value as WorkspaceType }))
            }
          >
            <SelectTrigger id='type' className='w-full'>
              <SelectValue placeholder='Select workspace type' />
            </SelectTrigger>
            <SelectContent>
              {Object.values(WORKSPACE_TYPE_CONFIGS).map((config) => (
                <SelectItem key={config.type} value={config.type}>
                  <div className='flex items-center gap-2'>
                    <config.icon className={`h-4 w-4 ${config.iconColor}`} />
                    <span>{config.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className='text-muted-foreground text-xs'>
            {typeConfig.description}
          </p>
        </div>
      )}

      {/* Workspace Name */}
      <div className='mb-6 space-y-2'>
        <label
          htmlFor='name'
          className='block text-sm leading-[14px] font-medium'
        >
          Workspace Name
        </label>
        <div className='flex w-full items-center gap-2'>
          {/* <EmojiPicker
            selectedIcon={data.icon}
            iconCategory={data.IconCategory}
            iconColor='#000000'
            onEmojiSelect={(emoji) => {
              setData((prev) => ({
                ...prev,
                icon: emoji,
                IconCategory: 'EMOJI',
              }))
            }}
            onIconChange={(iconName: string, iconCategory: IconCategory) => {
              if (iconCategory) {
                setData((prev) => ({
                  ...prev,
                  icon: iconName,
                  IconCategory: iconCategory,
                }))
              }
            }}
            onColorChange={(color: string) => {
              console.log('Selected color:', color)
            }}
            isPopoverMode={true}
            showColor={false}
          /> */}
          <Input id='name' value={data.name} onChange={handleNameChange} />
        </div>

        <p className='text-muted-foreground text-xs'>
          The name of your workspace.
        </p>
      </div>

      {/* Frameworks — RFC-231: the only question before the product appears */}
      <div className='mb-6 space-y-2'>
        <label className='block text-sm leading-[14px] font-medium'>
          Which frameworks are you working toward?
        </label>
        <div className='flex flex-col gap-2' role='group' aria-label='Frameworks'>
          {frameworkChoices.map((o) => {
            const checked = frameworks.includes(o.value)
            return (
              <label
                key={o.value}
                className='flex cursor-pointer items-start gap-3 rounded-sm border px-3 py-2 hover:bg-muted/40'
              >
                <Checkbox
                  checked={checked}
                  aria-label={o.label}
                  onCheckedChange={(v) =>
                    setFrameworks((prev) =>
                      v ? [...prev, o.value] : prev.filter((x) => x !== o.value),
                    )
                  }
                />
                <span>
                  <span className='block text-sm font-medium'>{o.label}</span>
                  <span className='text-muted-foreground text-xs'>{o.description}</span>
                </span>
              </label>
            )
          })}
        </div>
        <p className='text-muted-foreground text-xs'>
          Vera seeds your controls and compiles your tests from these. You can change them later.
        </p>
      </div>

      <div className='mb-10' />

      {/* Submit Button */}
      <Button
        className='w-full'
        onClick={() => {
          handleSubmit()
        }}
      >
        Next
        <ArrowRight size={16} />
      </Button>
    </div>
  )
}
